import React, { useEffect, useState } from "react";
import {
  ResponsiveContainer,
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  PieChart,
  Pie,
  Cell,
  Legend,
  BarChart,
  Bar,
} from "recharts";
import PageHeader from "@/components/shared/PageHeader";
import SectionCard from "@/components/shared/SectionCard";
import KPITile from "@/components/shared/KPITile";
import api, { extractApiError } from "@/lib/api";
import { Clock, Activity, AlertTriangle, Gauge } from "lucide-react";

const PIE_COLORS = ["#E11D48", "#EA580C", "#D97706", "#059669"];

function formatTrend(deltaPct) {
  const numeric = Number(deltaPct);
  if (!Number.isFinite(numeric)) return null;
  const prefix = numeric > 0 ? "+" : "";
  return {
    direction: numeric >= 0 ? "up" : "down",
    value: `${prefix}${numeric}%`,
  };
}

function formatMinutes(valueInSeconds) {
  const numeric = Number(valueInSeconds);
  if (!Number.isFinite(numeric)) return "—";
  return `${Math.round(numeric / 60)} min`;
}

function formatMonthLabel(isoDate) {
  if (!isoDate) return "";

  const date = new Date(isoDate);
  if (Number.isNaN(date.getTime())) return String(isoDate);

  return new Intl.DateTimeFormat("en-US", { month: "short" }).format(date);
}

function formatDayLabel(isoDate) {
  if (!isoDate) return "";

  const date = new Date(isoDate);
  if (Number.isNaN(date.getTime())) return String(isoDate);

  return new Intl.DateTimeFormat("en-US", { month: "short", day: "numeric" }).format(date);
}

function formatSeverityLabel(value) {
  const severity = String(value || "").trim().toLowerCase();
  if (!severity) return "";
  return severity.charAt(0).toUpperCase() + severity.slice(1);
}

export default function AnalyticsPage() {
  const [incidentSummary, setIncidentSummary] = useState(null);
  const [tripsSummary, setTripsSummary] = useState(null);
  const [accidentTrend, setAccidentTrend] = useState([]);
  const [severitySplit, setSeveritySplit] = useState([]);
  const [responseTimeTrend, setResponseTimeTrend] = useState([]);
  const [drivingScoreDistribution] = useState([]);
  const [error, setError] = useState("");

  useEffect(() => {
    let isMounted = true;

    const loadSummaries = async () => {
      setError("");

      try {
        const [incidentsResponse, tripsResponse, accidentTrendResponse, severityResponse, responseTimesResponse] = await Promise.all([
          api.get("/analytics/incidents/summary", { params: { period: "7d" } }),
          api.get("/analytics/trips/summary", { params: { period: "7d" } }),
          api.get("/analytics/incidents/timeseries", { params: { bucket: "month" } }),
          api.get("/analytics/incidents/by-severity"),
          api.get("/analytics/incidents/response-times", { params: { bucket: "day" } }),
        ]);

        if (!isMounted) return;

        setIncidentSummary(incidentsResponse?.data?.data || {});
        setTripsSummary(tripsResponse?.data?.data || {});
        setAccidentTrend(
          Array.isArray(accidentTrendResponse?.data?.series)
            ? accidentTrendResponse.data.series.map((item) => ({
                month: formatMonthLabel(item?.bucket),
                count: Number(item?.count || 0),
              }))
            : [],
        );
        setSeveritySplit(
          Array.isArray(severityResponse?.data?.buckets)
            ? severityResponse.data.buckets.map((item) => ({
                name: formatSeverityLabel(item?.severity),
                value: Number(item?.count || 0),
              }))
            : [],
        );
        setResponseTimeTrend(
          Array.isArray(responseTimesResponse?.data?.series)
            ? responseTimesResponse.data.series.map((item) => ({
                day: formatDayLabel(item?.bucket),
                police: Number(item?.police || 0),
                ambulance: Number(item?.ambulance || 0),
                fire: Number(item?.fire || 0),
              }))
            : [],
        );
        // responseTimesResponse.data.overall is available if we want to surface avg_response_minutes / avg_arrival_minutes later.
      } catch (err) {
        if (!isMounted) return;
        const { message } = extractApiError(err);
        setError(message || "Failed to load analytics summary.");
      }
    };

    loadSummaries();

    return () => {
      isMounted = false;
    };
  }, []);

  const avgResponse = incidentSummary?.avg_response_seconds;
  const criticalEvents = incidentSummary?.critical;
  const tripsToday = tripsSummary?.trips;

  return (
    <div className="space-y-6" data-testid="analytics-page">
      <PageHeader title="Analytics" subtitle="Driving trends, accidents and response performance." />

      {error && (
        <div className="rounded-xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-700">
          {error}
        </div>
      )}

      <div className="grid grid-cols-2 gap-3 lg:grid-cols-3">
        <KPITile
          icon={Clock}
          label="Avg Response"
          value={formatMinutes(avgResponse?.value)}
          accent="blue"
          trend={formatTrend(avgResponse?.delta_pct)}
        />
        <KPITile
          icon={Activity}
          label="Trips today"
          value={Number.isFinite(Number(tripsToday?.value)) ? Number(tripsToday.value).toLocaleString() : "—"}
          accent="indigo"
          trend={formatTrend(tripsToday?.delta_pct)}
        />
        <KPITile
          icon={AlertTriangle}
          label="Critical events"
          value={Number.isFinite(Number(criticalEvents?.value)) ? Number(criticalEvents.value).toLocaleString() : "—"}
          accent="rose"
          trend={formatTrend(criticalEvents?.delta_pct)}
        />
        {/* <KPITile icon={Gauge} label="Avg score" value="78" accent="emerald" trend={{ direction: "up", value: "+1.2%" }} /> */}
      </div>

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
        <SectionCard title="Response Time by Service" subtitle="Minutes (last 7 days)">
          <div className="h-72">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={responseTimeTrend}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                <XAxis dataKey="day" tick={{ fontSize: 11, fill: "#64748b" }} stroke="#e2e8f0" />
                <YAxis tick={{ fontSize: 11, fill: "#64748b" }} stroke="#e2e8f0" />
                <Tooltip contentStyle={{ borderRadius: 12, border: "1px solid #e2e8f0", fontSize: 12 }} />
                <Legend wrapperStyle={{ fontSize: 12 }} />
                <Line dataKey="police" stroke="#0369A1" strokeWidth={2} dot={{ r: 3 }} />
                <Line dataKey="ambulance" stroke="#18c20c" strokeWidth={2} dot={{ r: 3 }} />
                <Line dataKey="fire" stroke="#BE123C" strokeWidth={2} dot={{ r: 3 }} />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </SectionCard>

        <SectionCard title="Severity Distribution" subtitle="All incidents this month">
          <div className="h-72">
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie data={severitySplit} dataKey="value" nameKey="name" outerRadius={92} innerRadius={58}>
                  {severitySplit.map((_, i) => (
                    <Cell key={i} fill={PIE_COLORS[i]} />
                  ))}
                </Pie>
                <Tooltip contentStyle={{ borderRadius: 12, border: "1px solid #e2e8f0", fontSize: 12 }} />
                <Legend wrapperStyle={{ fontSize: 12 }} />
              </PieChart>
            </ResponsiveContainer>
          </div>
        </SectionCard>

        <SectionCard title="Driving Score Distribution" subtitle="Across all drivers">
          <div className="h-72">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={drivingScoreDistribution}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                <XAxis dataKey="score" tick={{ fontSize: 11, fill: "#64748b" }} stroke="#e2e8f0" />
                <YAxis tick={{ fontSize: 11, fill: "#64748b" }} stroke="#e2e8f0" />
                <Tooltip contentStyle={{ borderRadius: 12, border: "1px solid #e2e8f0", fontSize: 12 }} />
                <Bar dataKey="drivers" fill="#0F172A" radius={[8, 8, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </SectionCard>

        <SectionCard title="Accident Trend (6 months)" subtitle="Detected incidents per month">
          <div className="h-72">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={accidentTrend}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                <XAxis dataKey="month" tick={{ fontSize: 11, fill: "#64748b" }} stroke="#e2e8f0" />
                <YAxis tick={{ fontSize: 11, fill: "#64748b" }} stroke="#e2e8f0" />
                <Tooltip contentStyle={{ borderRadius: 12, border: "1px solid #e2e8f0", fontSize: 12 }} />
                <Line dataKey="count" stroke="#0F172A" strokeWidth={2.5} dot={{ r: 4 }} />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </SectionCard>
      </div>
    </div>
  );
}
